import {skipToken} from '@reduxjs/toolkit/query';

import {useState} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  AuctionVehicleDetailResponseBody,
  sourcingAuctionApi,
  useGetSourcingAuctionDetailQuery,
} from '@dms/api/core';

import {Nullish, parseDate, useTimeout} from 'shared/web';

import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {CarStatusFilter} from '../../../pages/AllOffers/types/CarStatusFilter';

/** How often a lot past its listed end is checked until the auction really ends. */
const PAST_END_POLLING_INTERVAL_MS = 30_000;
/** Longest delay a browser timer can wait; a lot ending later is not watched. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

const getEndTime = (carEndTime: string | Nullish) =>
  isNil(carEndTime) ? null : (parseDate(carEndTime)?.getTime() ?? null);

/** Milliseconds until the listed end, or null when there is nothing to wait for. */
const getDelayUntilEnd = (carEndTime: string | Nullish) => {
  const endTime = getEndTime(carEndTime);
  const delay = isNil(endTime) ? null : endTime - Date.now();

  return isNil(delay) || delay <= 0 || delay > MAX_TIMER_DELAY_MS ? null : delay;
};

const hasPassedEnd = (carEndTime: string | Nullish) => {
  const endTime = getEndTime(carEndTime);

  return isNotNil(endTime) && endTime <= Date.now();
};

/**
 * The auction lot of the preview, kept current without reloading the page. Lists load a lot once
 * and only the All Offers automatic update refreshes it, so elsewhere a lot that passes its
 * listed end would stay "extended" forever. From the listed end on, this fetches just this lot
 * again - and keeps checking it while it is still active - and lays the fresh data over the
 * listed one.
 */
export function useLiveAuction(auction: AuctionVehicleDetailResponseBody) {
  const currency = useSourcingCurrency()?.code;
  const buyerCountry = useSourcingTargetCountry()?.code;
  const pricingStrategy = useSourcingPricingStrategy();
  const listedVehicle = auction.auctionVehicle;

  const [isPastEnd, setIsPastEnd] = useState(() => hasPassedEnd(listedVehicle.carEndTime));
  // Counted once: the preview is keyed by the lot, so a new lot starts a new timer.
  const [delayUntilEnd] = useState(() => getDelayUntilEnd(listedVehicle.carEndTime));
  useTimeout(() => setIsPastEnd(true), isPastEnd ? null : delayUntilEnd);

  const isListedActive = listedVehicle.carStatus !== CarStatusFilter.ENDED;
  const externalId = listedVehicle.adId;

  const lotArgs =
    isPastEnd && isListedActive && isNotNil(externalId)
      ? {externalId, currency, buyerCountry, pricingStrategy}
      : skipToken;
  const {data} = sourcingAuctionApi.endpoints.getSourcingAuctionDetail.useQueryState(lotArgs);
  const freshVehicle = data?.auctionVehicle;

  useGetSourcingAuctionDetailQuery(lotArgs, {
    refetchOnMountOrArgChange: true,
    // Keep checking a lot that is still active; once it ended, the last answer stays.
    pollingInterval:
      freshVehicle?.carStatus === CarStatusFilter.ENDED ? 0 : PAST_END_POLLING_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });

  return isNil(freshVehicle)
    ? auction
    : {...auction, auctionVehicle: {...listedVehicle, ...freshVehicle}};
}
